import { useState } from "react"
import type { ComponentProps, FormEvent } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import CustomerPicker from "@/v2-backoffice-client/customer/CustomerPicker"
import CountryPicker from "@/v2-backoffice-client/country/CountryPicker"
import type { TenantCreateMutationVariables } from "@/v2-backoffice-client/tenant/create/__types__/tenant.create.mutation"

type FormData = TenantCreateMutationVariables["input"]

export type TenantCreateFormProps = Omit<ComponentProps<"form">, "onSubmit"> & {
  defaultValues?: FormData
  onSubmit(data: FormData, e: FormEvent): void
}

function TenantCreateForm(props: TenantCreateFormProps) {
  const { defaultValues, onSubmit, className, ...formProps } = props

  const [formData, setFormData] = useState<FormData>(() => ({
    Name: "",
    ExternalCustomerId: "",
    ...defaultValues,
  }))

  function handleChange(field: keyof FormData, value: string) {
    setFormData({ ...formData, [field]: value })
  }

  return (
    <form
      className={cn("space-y-4", className)}
      onSubmit={(e) => {
        onSubmit(formData, e)
      }}
      {...formProps}
    >
      <div className="space-y-2">
        <Label htmlFor="tenantName">Team Name</Label>
        <Input
          id="tenantName"
          type="text"
          required
          value={formData.Name}
          onChange={(e) => {
            handleChange("Name", e.target.value)
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="externalCustomerId">Customer</Label>
        <div className="flex items-center gap-2">
          <Input
            id="externalCustomerId"
            type="text"
            required
            className="flex-1"
            placeholder="ExternalCustomerId"
            value={formData.ExternalCustomerId}
            onChange={(e) => {
              handleChange("ExternalCustomerId", e.target.value)
            }}
          />
          <CustomerPicker
            // The team being created usually carries its customer's name —
            // seed the search so the match is one click.
            defaultSearch={formData.Name}
            onSelect={(customer) => {
              // App-created customers have no HubSpot id yet; teams still
              // link to customers through it, so those can't be picked.
              handleChange(
                "ExternalCustomerId",
                customer.ExternalCustomerId ?? "",
              )
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="tenantCountry">Country</Label>
        <CountryPicker
          className="w-full"
          value={formData.CountryCode ?? null}
          onValueChange={(value) => {
            setFormData({ ...formData, CountryCode: value })
          }}
          emptyLabel="Norway (default)"
          placeholder="Norway (default)"
          title="The country of the project created with this team."
        />
        <p className="text-xs text-muted-foreground">
          Every new team gets a project, and this is the country that
          project&apos;s data is scoped to. Members joining the team land in
          that project and take its country.
        </p>
      </div>
    </form>
  )
}

export default TenantCreateForm
